import { useSyncExternalStore } from 'react';
import type { DesktopCaptureJob, DesktopCaptureStart } from '@kiipu/desktop-protocol';
import { desktopBridge, platformState } from './client-platform';

const empty: DesktopCaptureJob[] = [];
let jobs = empty;
let revision = 0;
const listeners = new Set<() => void>();
const identity = () => {
  const state = platformState();
  return JSON.stringify([state?.apiOrigin, state?.user?.id, state?.sessionGeneration]);
};
const notify = () => {
  revision++;
  for (const listener of listeners) listener();
};
export function acceptDesktopCaptures(value: DesktopCaptureJob[]) {
  jobs = [...value].sort((left, right) => right.createdAt - left.createdAt);
  notify();
}
export function clearDesktopCaptures() {
  jobs = empty;
  notify();
}
export function rememberDesktopCapture(job: DesktopCaptureJob) {
  const existing = jobs.find((value) => value.id === job.id);
  if (existing && existing.updatedAt > job.updatedAt) return;
  acceptDesktopCaptures([...jobs.filter((value) => value.id !== job.id), job]);
}
export const desktopCapturesSnapshot = () => jobs;
export const subscribeDesktopCaptures = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const useDesktopCaptures = () =>
  useSyncExternalStore(subscribeDesktopCaptures, desktopCapturesSnapshot, () => empty);

/** The existing bootstrap owns the event subscription; this only loads an initial snapshot. */
export async function loadDesktopCaptures() {
  const bridge = desktopBridge();
  if (!platformState()?.user || typeof bridge?.listCaptures !== 'function') return;
  const owner = identity();
  const before = revision;
  const value = await bridge.listCaptures();
  if (owner === identity() && before === revision) acceptDesktopCaptures(value);
}
export async function startDesktopCapture(input: DesktopCaptureStart) {
  const bridge = desktopBridge();
  if (typeof bridge?.startCapture !== 'function')
    throw new Error('此桌面版本尚不支持本地保存，请更新 Kiipu。');
  const owner = identity();
  const job = await bridge.startCapture(input);
  if (owner !== identity()) throw new DOMException('Account changed during capture', 'AbortError');
  rememberDesktopCapture(job);
  window.dispatchEvent(new Event('kiipu:show-captures'));
  return job;
}
